import { useId, useRef, useState, type ReactNode } from 'react';

import { useFocusTrap, useOnEscape } from '../lib/hooks';

import { Button } from './Button';

/**
 * `xxl` is the working-dialog size (a file queue, a form) rather than a prompt.
 *
 * `chart` is the odd one out, and deliberately: it is for a dialog whose *subject*
 * is a visualisation, where the figure has to be big enough to read rather than
 * merely present. Viewport-relative for that reason — a fixed pixel width either
 * wastes a large screen or overflows a small one.
 */
const WIDTHS = { sm: 420, md: 440, lg: 470, xl: 480, xxl: 620, chart: 'min(880px, 92vw)' } as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: keyof typeof WIDTHS;
  /** Renders custom footer buttons instead of the confirm/cancel pair. */
  footer?: ReactNode;
  /** Style the title in the destructive colour. */
  destructive?: boolean;
  /** Allow Escape / scrim-click to dismiss. Off by default (mock modals don't). */
  dismissible?: boolean;
}

/**
 * A real dialog (a11y §8.6.5): `role="dialog"`, `aria-modal`, labelled title,
 * focus trap, focus restore and (optional) Escape. Background is inert to Tab via
 * the trap. Scrim-click only closes when `dismissible`.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  width = 'md',
  footer,
  destructive = false,
  dismissible = false,
}: ModalProps): React.JSX.Element | null {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);
  useOnEscape(open && dismissible, onClose);

  if (!open) return null;

  return (
    // The scrim is a mouse convenience; keyboard dismissal is Escape (wired above
    // when dismissible), and the dialog itself carries the accessible semantics.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="ds-scrim"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className="ds-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width: WIDTHS[width] }}
      >
        <h2 id={titleId} className={destructive ? 'ds-modal__title ds-modal__title--destructive' : 'ds-modal__title'}>
          {title}
        </h2>
        {children}
        {footer}
      </div>
    </div>
  );
}

export interface ConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Solid --bad primary + destructive title. */
  destructive?: boolean;
  /**
   * Double-confirm: the user must type `acknowledgeWord` (e.g. `ERASE`) before the
   * confirm button enables — the GDPR-erasure pattern (design-reference §4.11).
   */
  acknowledgeWord?: string;
  loading?: boolean;
}

/** A confirm dialog with a destructive variant and an optional type-to-confirm gate. */
export function ConfirmModal({
  open,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  acknowledgeWord,
  loading = false,
}: ConfirmModalProps): React.JSX.Element | null {
  const [ack, setAck] = useState('');
  const ackId = useId();
  const gated = acknowledgeWord !== undefined && ack.trim() !== acknowledgeWord;

  return (
    <Modal open={open} onClose={onCancel} title={title} destructive={destructive} dismissible>
      <div className="ds-modal__body">{children}</div>
      {acknowledgeWord !== undefined ? (
        <div className="ds-field" style={{ marginTop: 14 }}>
          <label className="ds-field__label" htmlFor={ackId}>
            Type <strong>{acknowledgeWord}</strong> to confirm
          </label>
          <input
            id={ackId}
            className="ds-input"
            value={ack}
            onChange={(event) => {
              setAck(event.currentTarget.value);
            }}
          />
        </div>
      ) : null}
      <div className="ds-modal__footer">
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'primary'}
          disabled={gated}
          loading={loading}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
